import type { Agent, CreateAgentPayload, GetAgentsPayload, UpdateAgentPayload } from '../../types/index.js';
import { baseApi } from '../base-api.js';

type AgentsResponse = { agents: Agent[] };
type AgentMutationResponse = { agent: Agent; message: string };

const agentListTags = (result: Agent[] | undefined) =>
  result
    ? [...result.map(({ id }) => ({ type: 'Agent' as const, id })), { type: 'Agent' as const, id: 'LIST' }]
    : [{ type: 'Agent' as const, id: 'LIST' }];

/**
 * Mirrors `apps/server/src/modules/agent` (`agentController`, prefix `/agents`, admin-only) —
 * admin-configurable AI prompt definitions used to generate evaluation-session conclusions
 * (`evaluationSessionController`'s `generate-conclusion` handler looks one up by slug via
 * `agentService.getAgentBySlug('evaluation-' + moduleSlug)`, live, at generation time). Nothing
 * embeds agent data elsewhere and no other domain's cache depends on it, so mutations here only
 * ever invalidate their own tag. No `GET /:id` route exists server-side, only list + mutations.
 */
export const agentApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getAgents: builder.query<Agent[], GetAgentsPayload>({
      query: (payload) => ({ path: ['agents'], method: 'get', query: payload }),
      transformResponse: (response: AgentsResponse) => response.agents,
      providesTags: (result) => agentListTags(result),
    }),

    createAgent: builder.mutation<Agent, CreateAgentPayload>({
      query: (body) => ({ path: ['agents'], method: 'post', body }),
      transformResponse: (response: AgentMutationResponse) => response.agent,
      invalidatesTags: [{ type: 'Agent', id: 'LIST' }],
    }),

    updateAgent: builder.mutation<Agent, { id: string; payload: UpdateAgentPayload }>({
      query: ({ id, payload }) => ({
        path: ['agents', { param: 'agent_id', value: id }],
        method: 'put',
        body: payload,
      }),
      transformResponse: (response: AgentMutationResponse) => response.agent,
      invalidatesTags: (_result, _error, { id }) => [
        { type: 'Agent', id },
        { type: 'Agent', id: 'LIST' },
      ],
    }),

    deleteAgent: builder.mutation<void, string>({
      query: (id) => ({ path: ['agents', { param: 'agent_id', value: id }], method: 'delete' }),
      transformResponse: (): void => undefined,
      invalidatesTags: (_result, _error, id) => [
        { type: 'Agent', id },
        { type: 'Agent', id: 'LIST' },
      ],
    }),
  }),
});

export const { useGetAgentsQuery, useCreateAgentMutation, useUpdateAgentMutation, useDeleteAgentMutation } = agentApi;
